/**
 * Zenprexi Bio-Risk™ Dashboard Types
 */

// =============================================================
// ENUMS
// =============================================================

export enum UserRole {
  WORKER = 'WORKER',
  SUPERVISOR = 'SUPERVISOR',
  ADMIN = 'ADMIN',
  CARRIER = 'CARRIER',
}

export enum CARIClassification {
  GREEN = 'GREEN',
  YELLOW = 'YELLOW',
  RED = 'RED',
}

export enum CredentialStatus {
  VERIFIED = 'VERIFIED',
  NOT_VERIFIED = 'NOT_VERIFIED',
  PROVISIONAL = 'PROVISIONAL',
  WITHHELD = 'WITHHELD',
}

export enum AlertType {
  FATIGUE_WARNING = 'FATIGUE_WARNING',
  BREAK_REQUIRED = 'BREAK_REQUIRED',
  STOP_WORK = 'STOP_WORK',
  SUPERVISOR_ALERT = 'SUPERVISOR_ALERT',
  EQUIPMENT_LOCKOUT = 'EQUIPMENT_LOCKOUT',
}

export enum AlertPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

// =============================================================
// AUTH TYPES
// =============================================================

export interface User {
  id: string;
  organizationId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
  workerId: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: User;
}

// =============================================================
// WORKER TYPES
// =============================================================

export interface Worker {
  id: string;
  organizationId: string;
  workerCode: string;
  department: string | null;
  jobTitle: string | null;
  shiftStart: string | null;
  shiftEnd: string | null;
  deviceId: string | null;
  industryProfile?: string | null;
  baselineHr: number | null;
  baselineHrv: number | null;
  currentCari: number | null;
  currentClassification: CARIClassification | null;
  lastReadingAt: string | null;
  isOnShift: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface WorkerWithScore extends Worker {
  cariScore: CARIScore | null;
}

// =============================================================
// CARI TYPES
// =============================================================

export interface CARIComponents {
  hrvComponent: number;
  hrComponent: number;
  movementComponent: number;
  sleepComponent: number;
}

export interface CARIScore {
  id: string;
  workerId: string;
  score: number;
  classification: CARIClassification;
  confidence: number;
  components: CARIComponents;
  trend: string | null;
  modelVersion?: string | null;
  cariV3?: CARIV3Payload | null;
  timestamp: string;
}

export interface CARIV3SignalQuality {
  confidence_tier?: 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW' | string;
  [key: string]: unknown;
}

export interface CARIV3Actuarial {
  wc_precursor_flags?: string[];
  [key: string]: unknown;
}

export interface CARIV3Payload {
  cari_v3?: Record<string, unknown>;
  signals?: Record<string, unknown>;
  weights_applied?: Record<string, unknown>;
  signal_quality?: CARIV3SignalQuality;
  actuarial?: CARIV3Actuarial;
  fatigue_accumulation_detail?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface CARIHistory {
  scores: CARIScore[];
  periodStart: string;
  periodEnd: string;
  averageScore: number;
  minScore: number;
  maxScore: number;
  timeInGreen: number;
  timeInYellow: number;
  timeInRed: number;
}

// =============================================================
// CREDENTIAL / SHADOW TYPES
// =============================================================

export interface Credential {
  id: string;
  workerId: string;
  band: CARIClassification | null;
  credentialStatus: CredentialStatus;
  validFrom: string;
  validUntil: string;
  bandingConfigVersion: string;
  isShadow: boolean;
  createdAt: string;
}

export interface CredentialStability {
  bandFlips24h: number;
  timeInBand24h: Record<string, number>;
}

export interface FleetCredentialWorker {
  workerId: string;
  workerCode: string;
  department: string | null;
  isOnShift: boolean;
  currentCari: number | null;
  currentClassification: CARIClassification | null;
  latestCredential: Credential | null;
  stability: CredentialStability;
}

export interface ShadowObservation {
  id: string;
  workerId: string;
  workerCode: string;
  observationType: string;
  band: CARIClassification | null;
  credentialStatus: CredentialStatus | null;
  triggerScore: number | null;
  baselineState: string | null;
  interventionPayload: Record<string, unknown> | null;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
}

export interface FleetCredentialOpsSummary {
  shadowModeEnabled: boolean;
  totalWorkers: number;
  credentialedWorkers: number;
  bandCounts: Record<string, number>;
  statusCounts: Record<string, number>;
  thresholdHitRate24h: number;
  baselineCompleteWorkers: number;
  provisionalWorkers: number;
  shadowObservations24h: number;
}

export interface FleetCredentialOps {
  summary: FleetCredentialOpsSummary;
  workers: FleetCredentialWorker[];
  shadowObservations: ShadowObservation[];
}

export interface PilotReadinessCheck {
  key: string;
  label: string;
  status: 'pass' | 'review' | 'block' | string;
  value: unknown;
  target: unknown;
  detail: string;
}

export interface PilotKnownGap {
  key: string;
  label: string;
  detail: string;
}

export interface PilotReadiness {
  organizationId: string;
  generatedAt: string;
  shadowModeEnabled: boolean;
  overallStatus: string;
  readyToActivate: boolean;
  metrics: Record<string, unknown>;
  checks: PilotReadinessCheck[];
  knownGaps: PilotKnownGap[];
}

// =============================================================
// ALERT/INTERVENTION TYPES
// =============================================================

export interface Alert {
  id: string;
  workerId: string;
  alertType: AlertType;
  priority: AlertPriority;
  triggerScore: number;
  message: string | null;
  recommendation: string | null;
  acknowledged: boolean;
  acknowledgedAt: string | null;
  escalated: boolean;
  resolved: boolean;
  createdAt: string;
}

// =============================================================
// FLEET TYPES
// =============================================================

export interface FleetSummary {
  organizationId: string;
  totalWorkers: number;
  activeWorkers: number;
  onShiftWorkers: number;
  greenCount: number;
  yellowCount: number;
  redCount: number;
  averageCari: number;
  activeAlerts: number;
  confidenceDistribution?: Record<string, number> | null;
  wcFlagSummary?: Record<string, number> | null;
  timestamp: string;
}

export interface FleetWorkerStatus {
  workerId: string;
  workerCode: string;
  department: string | null;
  currentCari: number | null;
  classification: CARIClassification | null;
  isOnShift: boolean;
  lastReadingAt: string | null;
  activeAlerts: number;
}

export interface FleetDashboard {
  summary: FleetSummary;
  workers: FleetWorkerStatus[];
  recentAlerts: Alert[];
}

export interface Department {
  department: string;
  totalWorkers: number;
  onShiftWorkers: number;
  averageCari: number | null;
}

export interface WebhookConfig {
  url: string | null;
  secretConfigured: boolean;
  events: string[];
}

export interface UpdateWebhookConfigRequest {
  url: string;
  secret?: string;
  events: string[];
}

// =============================================================
// WEBSOCKET TYPES
// =============================================================

export interface WSMessage {
  type: 'cari_update' | 'alert' | 'worker_status' | 'heartbeat';
  data: unknown;
  timestamp: string;
}

export interface CARIUpdateMessage {
  workerId: string;
  workerCode: string;
  score: number;
  classification: CARIClassification;
  previousScore: number | null;
}

// =============================================================
// API RESPONSE TYPES
// =============================================================

export interface ApiError {
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
